import { useNavigate } from "react-router-dom";
import { num } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertTriangle, OctagonAlert, Info, Siren, CalendarDays, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { OccupancyBar, dayLabel, accommodationUrl } from "./AccommodationBits";

const SEV_ICON = { danger: OctagonAlert, warn: AlertTriangle, info: Info };
const SEV_TONE = { danger: "text-rose-600 dark:text-rose-400", warn: "text-amber-700 dark:text-amber-300", info: "text-muted-foreground" };
const worst = (alerts = []) => (alerts.some((a) => a.severity === "danger") ? "danger" : alerts.some((a) => a.severity === "warn") ? "warn" : alerts.length ? "info" : null);

export const campShortName = (name = "") => name.split(" – ").slice(-1)[0];
export const occupancyTone = (pct) => (pct >= 95 ? "danger" : pct >= 85 ? "warning" : undefined);

/** Compact alert summary: icon for the worst severity + first alert text, rest in tooltip. */
export const CampAlerts = ({ alerts = [], testId, className }) => {
  const sev = worst(alerts);
  if (!sev) return <span className={cn("text-[12px] text-muted-foreground", className)} data-testid={testId}>Ingen varsler</span>;
  const Icon = SEV_ICON[sev];
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={cn("inline-flex min-w-0 items-center gap-1.5 text-[12px]", className)} data-testid={testId}>
            <Icon className={cn("h-3.5 w-3.5 shrink-0", SEV_TONE[sev])} />
            <span className="truncate text-foreground/85">{alerts[0].text}</span>
            {alerts.length > 1 && <span className="shrink-0 font-mono text-[11px] text-muted-foreground">+{alerts.length - 1}</span>}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[320px] text-[12px]">
          <ul className="space-y-1">{alerts.map((a, i) => <li key={i}>{a.text}</li>)}</ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const Operator = ({ camp }) => (camp.has_person_data ? <span>{camp.operator?.name || "Stord"}</span> : <span className="text-muted-foreground">{camp.operator?.name ? `${camp.operator.name} (kunde)` : "Kunde drifter"}</span>);

/** Desktop table (design_guidelines → portfolio_overview.camp_list): belegg bar with muted track + graphite fill. */
export const CampTable = ({ camps = [], className }) => {
  const navigate = useNavigate();
  return (
    <div className={cn("overflow-x-auto", className)}>
      <table className="w-full text-[13px]" data-testid="accommodation-camps-table">
        <thead className="text-[11px] uppercase tracking-wide text-muted-foreground">
          <tr className="border-b">
            <th className="py-2 pr-3 text-left font-medium">Camp</th>
            <th className="py-2 pr-3 text-left font-medium">Driftes av</th>
            <th className="py-2 pr-3 text-right font-medium">Senger</th>
            <th className="w-[200px] py-2 pr-3 text-left font-medium">Belegg</th>
            <th className="whitespace-nowrap py-2 pr-3 text-right font-medium">7 dager</th>
            <th className="whitespace-nowrap py-2 pr-3 text-left font-medium">Neste bytte</th>
            <th className="py-2 pr-3 text-left font-medium">Varsler</th>
            <th className="py-2 text-right font-medium" aria-label="Handlinger" />
          </tr>
        </thead>
        <tbody>
          {camps.map((c) => {
            const k = c.kpis;
            const tracked = c.has_person_data;
            return (
              <tr key={c.project_id} className="group border-b border-border/70 transition-colors duration-150 hover:bg-muted/40" data-testid={`accommodation-camp-row-${c.project_id}`}>
                <td className="py-2.5 pr-3">
                  <button type="button" onClick={() => navigate(accommodationUrl(c.project_id))} className="text-left focus-visible:outline-none focus-visible:underline" data-testid={`accommodation-camp-open-button-${c.project_id}`}>
                    <span className="block font-medium leading-5">{campShortName(c.name)}</span>
                    <span className="block text-[11.5px] leading-4 text-muted-foreground"><span className="font-mono">{c.project_number}</span>{c.customer_name ? ` · ${c.customer_name}` : ""}</span>
                  </button>
                </td>
                <td className="py-2.5 pr-3 text-[12.5px]"><Operator camp={c} /></td>
                <td className="py-2.5 pr-3 text-right font-mono tabular">
                  <span className="font-semibold">{num(k.beds_total)}</span>
                  {k.beds_out ? <span className="block text-[11px] text-rose-600 dark:text-rose-400">{k.beds_out} ute</span> : <span className="block text-[11px] text-muted-foreground">{k.rooms_total} rom</span>}
                </td>
                <td className="py-2.5 pr-3">
                  {tracked ? (
                    <div className="flex items-center gap-2">
                      <OccupancyBar pct={k.occupancy_pct} tone={occupancyTone(k.occupancy_pct)} className="w-24" testId={`accommodation-camp-occupancy-${c.project_id}`} />
                      <span className="whitespace-nowrap font-mono text-[12px] tabular">{num(k.on_site)}/{num(k.beds_available)} <span className="text-muted-foreground">· {k.occupancy_pct} %</span></span>
                    </div>
                  ) : <span className="text-[12px] text-muted-foreground">Ikke ført her</span>}
                </td>
                <td className="whitespace-nowrap py-2.5 pr-3 text-right font-mono text-[12px] tabular">
                  {tracked ? <><span className={cn(k.arrivals_7d && "text-foreground")}>{k.arrivals_7d} inn</span> <span className="text-muted-foreground">·</span> <span className={cn(k.departures_7d && "text-foreground")}>{k.departures_7d} ut</span></> : <span className="text-muted-foreground">–</span>}
                </td>
                <td className="whitespace-nowrap py-2.5 pr-3 text-[12px]">
                  {c.next_handover ? <><span className="font-medium">{dayLabel(c.next_handover.date)}</span> <span className={cn("font-mono tabular", c.next_handover.net > 0 ? "text-amber-700 dark:text-amber-300" : "text-muted-foreground")}>{c.next_handover.net > 0 ? "+" : ""}{c.next_handover.net}</span></> : <span className="text-muted-foreground">–</span>}
                </td>
                <td className="max-w-[220px] py-2.5 pr-3"><CampAlerts alerts={c.alerts} testId={`accommodation-camp-alerts-${c.project_id}`} /></td>
                <td className="py-2.5 text-right">
                  <div className="inline-flex items-center gap-1">
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-[12px]" onClick={() => navigate(accommodationUrl(c.project_id, "idag"))} data-testid={`accommodation-camp-today-button-${c.project_id}`}><CalendarDays className="h-3.5 w-3.5" /> I dag</Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-[12px]" onClick={() => navigate(`/prosjekter/${c.project_id}/beredskap`)} data-testid={`accommodation-camp-emergency-button-${c.project_id}`}><Siren className="h-3.5 w-3.5 text-rose-600" /> Beredskap</Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/** Stacked rows for mobile and for the scoped operator dashboard. */
export const CampCards = ({ camps = [], className, dense }) => {
  const navigate = useNavigate();
  return (
    <ul className={cn("divide-y divide-border/70", className)} data-testid="accommodation-camp-cards">
      {camps.map((c) => {
        const k = c.kpis;
        return (
          <li key={c.project_id} className={cn("py-3", dense && "py-2.5")} data-testid={`accommodation-camp-card-${c.project_id}`}>
            <div className="flex items-start justify-between gap-3">
              <button type="button" onClick={() => navigate(accommodationUrl(c.project_id))} className="min-w-0 text-left focus-visible:outline-none focus-visible:underline" data-testid={`camp-card-open-${c.project_id}`}>
                <span className="flex items-center gap-1 text-[14px] font-medium leading-5">{campShortName(c.name)} <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground" /></span>
                <span className="block truncate text-[12px] leading-4 text-muted-foreground"><span className="font-mono">{c.project_number}</span>{c.customer_name ? ` · ${c.customer_name}` : ""} · <Operator camp={c} /></span>
              </button>
              <span className="shrink-0 text-right font-mono text-[13px] tabular">
                {c.has_person_data ? <><span className="font-semibold">{num(k.on_site)}</span><span className="text-muted-foreground">/{num(k.beds_available)}</span></> : <span className="font-semibold">{num(k.beds_total)}</span>}
                <span className="block text-[11px] text-muted-foreground">{c.has_person_data ? `${k.occupancy_pct} % belegg` : "senger"}</span>
              </span>
            </div>
            {c.has_person_data && <OccupancyBar pct={k.occupancy_pct} tone={occupancyTone(k.occupancy_pct)} className="mt-2.5" testId={`camp-card-occupancy-${c.project_id}`} />}
            <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[12px] text-muted-foreground">
              {c.has_person_data && <span className="font-mono tabular">{k.arrivals_7d} inn · {k.departures_7d} ut <span className="font-sans">neste 7 d</span></span>}
              {c.next_handover && <span>Bytte {dayLabel(c.next_handover.date)} <span className="font-mono tabular">{c.next_handover.net > 0 ? "+" : ""}{c.next_handover.net}</span></span>}
              <CampAlerts alerts={c.alerts} testId={`camp-card-alerts-${c.project_id}`} className="basis-full" />
            </div>
            <div className="mt-2.5 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="h-8" onClick={() => navigate(accommodationUrl(c.project_id, "romkart"))} data-testid={`camp-card-rooms-button-${c.project_id}`}>Romkart</Button>
              <Button size="sm" variant="outline" className="h-8" onClick={() => navigate(accommodationUrl(c.project_id, "idag"))} data-testid={`camp-card-today-button-${c.project_id}`}><CalendarDays className="h-3.5 w-3.5" /> I dag</Button>
              <Button size="sm" variant="outline" className="h-8" onClick={() => navigate(`/prosjekter/${c.project_id}/beredskap`)} data-testid={`camp-card-emergency-button-${c.project_id}`}><Siren className="h-3.5 w-3.5 text-rose-600" /> Beredskap</Button>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

/** «Neste 7 dager» – arrivals/departures grouped by date → camp → employer (counts only; names live in the project view). */
export const UpcomingList = ({ days = [], className, testId = "accommodation-upcoming" }) => {
  const navigate = useNavigate();
  if (!days.length) return <p className={cn("text-[12.5px] text-muted-foreground", className)} data-testid={`${testId}-empty`}>Ingen ankomster eller avreiser neste 7 dager.</p>;
  return (
    <ol className={cn("space-y-3", className)} data-testid={testId}>
      {days.map((d) => (
        <li key={d.date} data-testid={`${testId}-day-${d.date}`}>
          <div className="flex items-baseline justify-between text-[12px]">
            <span className="font-medium text-foreground/90">{dayLabel(d.date)}</span>
            <span className="font-mono tabular text-muted-foreground">{d.arrivals ? `${d.arrivals} inn` : ""}{d.arrivals && d.departures ? " · " : ""}{d.departures ? `${d.departures} ut` : ""}</span>
          </div>
          <ul className="mt-1 space-y-0.5">
            {d.groups.map((g, i) => (
              <li key={i}>
                <button type="button" onClick={() => navigate(`${accommodationUrl(g.project_id, "idag")}&date=${d.date}`)} className="-mx-1 flex w-[calc(100%+0.5rem)] items-center gap-2 rounded px-1 py-0.5 text-left text-[12.5px] transition-colors duration-150 hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none" data-testid={`${testId}-group-${d.date}-${i}`}>
                  <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", g.kind === "arrival" ? "bg-foreground/70" : "bg-border")} aria-hidden />
                  <span className="min-w-0 flex-1 truncate"><span className="font-medium">{g.camp}</span> <span className="text-muted-foreground">· {g.employer_name || "Uten arbeidsgiver"}</span></span>
                  <span className="shrink-0 font-mono text-[11.5px] tabular text-muted-foreground">{g.count} {g.kind === "arrival" ? "inn" : "ut"}</span>
                </button>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ol>
  );
};
